import DOMPurify from 'dompurify';
import Excel from 'exceljs';

import {
  CellType,
  SheetToModelMapping,
  SpreadSheetModelParser,
} from '@/common/components/controls/table/bulk-create/model-parser/ModelParser';
import TableColumn from '@/common/components/controls/table/TableColumn';
import { ITableFilter } from '@/common/components/controls/table/TableFilter';
import UserView from '@/common/components/controls/table/TableUserSettings/UserView';
import FormControl from '@/common/components/generic-form/form-controls/FormControl';
import ControlValidator from '@/common/components/generic-form/form-controls/validator/CustomValidator';
import { getAppInstance, i } from '@/globals';
import Validator from '@/validator/Validator';
import i18n from '@/vueI18n';

type ViewSync = Pick<UserView, 'name' | 'value'>;

export default class SpreadSheetUserViewParser extends SpreadSheetModelParser<UserView> {
  public userViews: UserView[] = [];
  public availableFilters: ITableFilter[] = [];

  public modelName = 'userView';
  public templatePath = '/assets/sheets/user-views-template.xlsx';
  public worksheetName = 'views';
  public tableColumns: Partial<Record<keyof UserView, TableColumn>> = {
    name: TableColumn.fromObject({
      key: 'name',
      label: i('names.name'),
      allowSort: false,
    }),
    value: TableColumn.fromObject({
      key: 'value',
      label: i('names.value'),
      allowSort: false,
    }),
  };
  protected headerRange = { from: 'A1', to: 'B1' };
  protected excelMapping: SheetToModelMapping<ViewSync> = {
    name: 'A',
    value: 'B',
  };
  private readonly addView: (value: UserView) => Promise<void>;

  constructor(userViews: UserView[], availableFilters: ITableFilter[], addView: (value: UserView) => Promise<void>) {
    super();

    this.userViews = userViews;
    this.availableFilters = availableFilters;
    this.addView = addView;
  }

  public parseRow(row: Excel.Row): FormControl<UserView> {
    const userView = new UserView({
      name: this.parseCell(row, 'name', CellType.String),
      value: this.parseCell(row, 'value', CellType.String),
      isDefault: false,
    });

    const nameValidator: ControlValidator<UserView> = async () => {
      if (userView.name === null) {
        return null;
      }

      // user views names need to be unique, check in the already existing ones, and the ones in the spreadsheet
      if (this.userViews.find((view) => userView.name === view.name)) {
        return {
          name: `${i('messages.viewWithSameName')}: ${userView.name}`,
        };
      }

      return null;
    };

    const valueValidator: ControlValidator<UserView> = async () => {
      const error = {
        value: i18n.global.t('messages.viewValueInvalid', [userView.name]),
      };

      const purify = DOMPurify(window);
      const purifiedValue = purify.sanitize(userView.value);

      if (!purifiedValue) {
        return error;
      }

      try {
        await userView.getSummary(this.availableFilters);
        return null;
      } catch (e) {
        return error;
      }
    };

    const validator = new Validator(getAppInstance());
    validator.init(userView);

    return new FormControl(userView, [nameValidator, valueValidator]);
  }

  public createEntity(entity: UserView): Promise<void> {
    return this.addView(entity);
  }
}
